import React, { useEffect, useRef, useState } from "react";

import { Box, Chip, TextField, Typography } from "@mui/material";

import { ClipLoader } from "react-spinners";

import { getTestRun, startTestByMapping } from "helpers/leads/testRun";

import StandartButton from "components/atoms/StandartButton";
import { GenericModal } from "components/GenericModal";

const POLL_INITIAL_INTERVAL_MS = 2000;
const POLL_MAX_INTERVAL_MS = 5000;
const POLL_BACKOFF_FACTOR = 1.3;
const POLL_TIMEOUT_MS = 90000;

const TERMINAL_STATUSES = ["COMPLETED", "FAILED", "EXPIRED"];

const PLATFORMS_REQUIRING_TEST_DATA = ["lnkdn", "sapi"];

const getStatusColor = (status) => {
    if (status === "COMPLETED") return "success";
    if (status === "FAILED" || status === "EXPIRED") return "error";
    return "warning";
};

const JsonSection = ({ label, description, value }) => {
    const hasValue =
        value !== null &&
        value !== undefined &&
        !(typeof value === "object" && Object.keys(value).length === 0);

    if (!hasValue) return null;

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {label}
            </Typography>
            {description ? (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.5 }}
                >
                    {description}
                </Typography>
            ) : null}
            <Box
                component="pre"
                sx={{
                    m: 0,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: "#f5f5f5",
                    fontSize: 12,
                    lineHeight: 1.5,
                    overflow: "auto",
                    maxHeight: 240,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                }}
            >
                {typeof value === "string"
                    ? value
                    : JSON.stringify(value, null, 2)}
            </Box>
        </Box>
    );
};

const TestRunModal = ({
    open,
    onClose,
    mappingIds = [],
    platformCode,
    sourceKeys = [],
    language,
    onRunningChange,
}) => {
    const needsTestData = PLATFORMS_REQUIRING_TEST_DATA.includes(platformCode);

    // "input" | "running" | "result"
    const [phase, setPhase] = useState(needsTestData ? "input" : "running");
    const [testDataValues, setTestDataValues] = useState({});
    const [result, setResult] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [timedOut, setTimedOut] = useState(false);

    const timeoutRef = useRef(null);
    const startTimeRef = useRef(null);
    const pollingActiveRef = useRef(false);

    const activeTestIdRef = useRef(null);
    const activeSignatureRef = useRef(null);

    const buildSignature = () =>
        JSON.stringify({
            mappingIds: [...(mappingIds || [])].sort(),
            platformCode,
        });

    const clearPolling = () => {
        pollingActiveRef.current = false;
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    const pollOnce = async (id) => {
        if (Date.now() - startTimeRef.current >= POLL_TIMEOUT_MS) {
            clearPolling();
            setTimedOut(true);
            setPhase("result");
            return true;
        }
        const response = await getTestRun(id);
        if (response?.status && response?.data) {
            setResult(response.data);
            if (TERMINAL_STATUSES.includes(response.data.status)) {
                clearPolling();
                setPhase("result");
                return true;
            }
        }
        return false;
    };

    const startPolling = (id) => {
        clearPolling();
        pollingActiveRef.current = true;
        startTimeRef.current = Date.now();

        let delay = POLL_INITIAL_INTERVAL_MS;

        const poll = async () => {
            const done = await pollOnce(id);
            if (done || !pollingActiveRef.current) return;
            timeoutRef.current = setTimeout(poll, delay);
            delay = Math.min(delay * POLL_BACKOFF_FACTOR, POLL_MAX_INTERVAL_MS);
        };

        poll();
    };

    const runTest = async (testData) => {
        setErrorMessage(null);
        setTimedOut(false);
        setResult(null);
        setPhase("running");

        const payload = {
            mapping_id: mappingIds,
            ...(testData ? { test_data: testData } : {}),
        };

        const response = await startTestByMapping(payload);

        if (!response?.status || !response?.data?.id) {
            setErrorMessage(
                response?.message ??
                    language?.lead?.test_run_failed ??
                    "Failed to start the test run.",
            );
            setPhase("result");
            return;
        }

        activeTestIdRef.current = response.data.id;
        activeSignatureRef.current = buildSignature();

        setResult(response.data);

        if (TERMINAL_STATUSES.includes(response.data.status)) {
            setPhase("result");
            return;
        }

        startPolling(response.data.id);
    };

    useEffect(() => {
        if (!open) {
            clearPolling();
            return;
        }

        const signature = buildSignature();
        const hasActiveRun =
            activeTestIdRef.current != null &&
            activeSignatureRef.current === signature;

        if (hasActiveRun) {
            setErrorMessage(null);
            setTimedOut(false);

            if (result && TERMINAL_STATUSES.includes(result.status)) {
                setPhase("result");
            } else {
                setPhase("running");
                startPolling(activeTestIdRef.current);
            }
            return () => clearPolling();
        }

        activeTestIdRef.current = null;
        activeSignatureRef.current = null;
        setResult(null);
        setErrorMessage(null);
        setTimedOut(false);
        setTestDataValues({});
        if (needsTestData) {
            setPhase("input");
        } else {
            runTest();
        }
        return () => clearPolling();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        onRunningChange?.(open && phase === "running");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, phase]);

    const handleRunWithTestData = () => {
        const testData = sourceKeys.reduce((acc, key) => {
            acc[key] = testDataValues[key] ?? "";
            return acc;
        }, {});
        runTest(testData);
    };

    const renderInput = () => (
        <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {language?.lead?.test_run_input_description ??
                    "Provide sample values for your source fields. This payload will be used to simulate an incoming lead."}
            </Typography>
            {sourceKeys.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {language?.lead?.test_run_no_source_fields ??
                        "No source fields available from the current mappings."}
                </Typography>
            ) : (
                sourceKeys.map((key) => (
                    <TextField
                        key={key}
                        fullWidth
                        size="small"
                        label={key}
                        value={testDataValues[key] ?? ""}
                        onChange={(e) =>
                            setTestDataValues((prev) => ({
                                ...prev,
                                [key]: e.target.value,
                            }))
                        }
                        sx={{ mb: 2 }}
                    />
                ))
            )}
        </Box>
    );

    const renderRunning = () => (
        <Box
            sx={{
                p: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 200,
                gap: 2,
            }}
        >
            <ClipLoader color="#000000" size={40} />
            <Typography variant="body2" color="text.secondary">
                {language?.lead?.test_run_running ??
                    "Running test, please wait..."}
            </Typography>
        </Box>
    );

    const renderResult = () => {
        if (errorMessage) {
            return (
                <Box sx={{ p: 3 }}>
                    <Typography color="error" variant="body2">
                        {errorMessage}
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ p: 3 }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {language?.lead?.test_run_status ?? "Status"}:
                    </Typography>
                    {result?.status ? (
                        <Chip
                            size="small"
                            label={result.status}
                            color={getStatusColor(result.status)}
                        />
                    ) : null}
                </Box>

                {timedOut ? (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                    >
                        {language?.lead?.test_run_timeout ??
                            "The test is still processing. Showing the latest available result."}
                    </Typography>
                ) : null}

                {result?.error_message ? (
                    <Box sx={{ mb: 2 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600 }}
                            color="error"
                        >
                            {language?.lead?.test_run_error ?? "Error"}
                        </Typography>
                        <Typography variant="body2" color="error">
                            {result.error_message}
                        </Typography>
                    </Box>
                ) : null}

                <JsonSection
                    label={
                        language?.lead?.test_run_incoming ?? "Incoming request"
                    }
                    description={
                        language?.lead?.test_run_incoming_desc ??
                        "The raw request received from the source."
                    }
                    value={result?.incoming_request}
                />
                <JsonSection
                    label={
                        language?.lead?.test_run_processed ?? "Processed data"
                    }
                    description={
                        language?.lead?.test_run_processed_desc ??
                        "The lead data after applying your field mappings."
                    }
                    value={result?.processed_data}
                />
                <JsonSection
                    label={
                        language?.lead?.test_run_outgoing_req ??
                        "Outgoing request"
                    }
                    description={
                        language?.lead?.test_run_outgoing_req_desc ??
                        "The request sent to the destination."
                    }
                    value={result?.outgoing_request}
                />
                <JsonSection
                    label={
                        language?.lead?.test_run_outgoing_res ??
                        "Outgoing response"
                    }
                    description={
                        language?.lead?.test_run_outgoing_res_desc ??
                        "The response returned by the destination."
                    }
                    value={result?.outgoing_response}
                />
            </Box>
        );
    };

    let content = null;
    if (phase === "input") content = renderInput();
    else if (phase === "running") content = renderRunning();
    else content = renderResult();

    const footer =
        phase === "input" ? (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <StandartButton
                    variant="contained"
                    color="primary"
                    onClick={handleRunWithTestData}
                    disabled={sourceKeys.length === 0}
                >
                    {language?.lead?.test_run_run ?? "Run test"}
                </StandartButton>
            </Box>
        ) : null;

    return (
        <GenericModal
            open={open}
            onClose={onClose}
            title={language?.lead?.test_run_title ?? "Test connection"}
            subtitle={
                language?.lead?.test_run_subtitle ??
                "Run a test using your current field mappings."
            }
            maxWidth="sm"
            content={content}
            footer={footer}
        />
    );
};

export default TestRunModal;
